import type { ValidationIssue } from '../types';

/**
 * Detect "dictation not understood" markers in a generated report.
 *
 * The report author is instructed (backend llm_service) to render garbled
 * dictation as `[unclear: "..."]` rather than guessing. This scans the
 * generated report for those markers (plus a few looser bracket/(?) forms
 * models occasionally emit) and turns each into a ValidationIssue so the
 * issues panel can take the radiologist straight to the spot. Detection is
 * read-only — the report content is never modified.
 */

const MARKER_PATTERNS = [
  /\[unclear[^\]]*\]/gi,          // [unclear: "..."] — the instructed form
  /\[inaudible[^\]]*\]/gi,        // [inaudible ...]
  /\[[^\]\n]{0,60}\?\s*\]/g,      // short bracketed span containing a ?
  /\(\?+\)/g,                     // (?) / (??)
];

function htmlToText(html: string): string {
  const el = document.createElement('div');
  el.innerHTML = html;
  return el.textContent || '';
}

export function detectDictationGaps(reportHtml: string): ValidationIssue[] {
  const text = htmlToText(reportHtml);
  const seen = new Set<string>();
  const markers: string[] = [];

  for (const pattern of MARKER_PATTERNS) {
    for (const match of text.matchAll(pattern)) {
      const marker = match[0];
      if (!seen.has(marker.toLowerCase())) {
        seen.add(marker.toLowerCase());
        markers.push(marker);
      }
    }
  }

  return markers.map((marker, i) => ({
    id: `dictation-gap-${i}`,
    category: 'dictation',
    severity: 'warning',
    message: 'Dictation not understood',
    details: `The dictation could not be confidently heard here and was left marked in the report: ${marker}`,
    location: marker,
    auto_fixable: false,
  }));
}
